import {
  Box, Chip, Dialog, DialogContent, DialogTitle, IconButton, Stack,
  Table, TableBody, TableCell, TableRow, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { LogEntry } from '../../api/types';
import { sevColor, sevName, srcColor } from '../../theme';

interface Props {
  entry: LogEntry | null;
  onClose: () => void;
}

const fmtUk = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London', hour12: false,
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit',
});

function fmtTimeBoth(iso: string) {
  const d = new Date(iso);
  const parts = fmtUk.formatToParts(d).reduce<Record<string, string>>((a, p) => (a[p.type] = p.value, a), {});
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second} (UK) — ${d.toISOString()} (UTC)`;
}

export function LogEntryDialog({ entry, onClose }: Props) {
  return (
    <Dialog open={!!entry} onClose={onClose} maxWidth="md" fullWidth>
      {entry && (
        <>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
            <Chip label={entry.source} size="small" sx={{ bgcolor: srcColor(entry.source), color: '#fff', fontWeight: 700 }} />
            <Chip label={sevName(entry.severity)} size="small" sx={{ bgcolor: sevColor(entry.severity), color: '#fff', fontWeight: 700 }} />
            <Typography component="span" sx={{ fontSize: 14, fontWeight: 600 }}>{entry.category}</Typography>
            <Box sx={{ flexGrow: 1 }} />
            <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <Table size="small">
                <TableBody>
                  <KvRow k="Time" v={fmtTimeBoth(entry.timestampUtc)} />
                  <KvRow k="Source" v={entry.source} />
                  <KvRow k="Severity" v={`${sevName(entry.severity)} (${entry.severity})`} />
                  <KvRow k="Kind" v={entry.kind} />
                  <KvRow k="Category" v={entry.category} />
                  <KvRow k="Correlation id" v={entry.correlationId || '—'} />
                  {entry.details && Object.entries(entry.details)
                    .filter(([k, v]) => v && k !== 'stackTrace')
                    .map(([k, v]) => <KvRow key={k} k={k} v={String(v)} />)}
                </TableBody>
              </Table>

              <Box>
                <Typography variant="overline" color="text.secondary">Message</Typography>
                <Box component="pre" sx={preSx}>{entry.message}</Box>
              </Box>

              {entry.details?.stackTrace && (
                <Box>
                  <Typography variant="overline" color="text.secondary">Stack trace</Typography>
                  <Box component="pre" sx={preSx}>{entry.details.stackTrace}</Box>
                </Box>
              )}
            </Stack>
          </DialogContent>
        </>
      )}
    </Dialog>
  );
}

function KvRow({ k, v }: { k: string; v: string }) {
  return (
    <TableRow>
      <TableCell sx={{ width: 160, color: 'text.secondary', textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5, border: 0, py: 0.5 }}>{k}</TableCell>
      <TableCell sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 13, wordBreak: 'break-all', whiteSpace: 'pre-wrap', border: 0, py: 0.5 }}>{v}</TableCell>
    </TableRow>
  );
}

const preSx = {
  bgcolor: 'background.default', border: 1, borderColor: 'divider', borderRadius: 1,
  p: 1.5, fontSize: 12, m: 0, whiteSpace: 'pre-wrap' as const, wordBreak: 'break-word' as const,
  maxHeight: 280, overflow: 'auto', fontFamily: 'ui-monospace, monospace',
};
